import React from 'react';
import { Helmet } from 'react-helmet';
import InfiniteScroll from 'react-infinite-scroller';
import { FormattedMessage } from 'react-intl';
import { Skeleton } from '@material-ui/lab';
import { Box, makeStyles, Typography } from '@popmenu/common-ui';
import { useRootPlacePersonalizedMenuItemsQuery } from '~/libs/gql/queries/eat/rootPlacePersonalizedMenuItemsQuery.generated';
import { useEatSession } from '../../hooks/useEatSession';
import { LOAD_MORE_THRESHOLD, useLoadMoreOnce } from '../../hooks/useLoadMoreOnce';
import { useRefinePath } from '../../hooks/useRefinePath';
import { useRouteParams } from '../../hooks/useRouteParams';
import { useSignInPath } from '../../hooks/useSignInPath';
import { DishFeedTabs } from '../DishFeedTabs';
import { DishFeedTitle } from '../DishFeedTitle';
import { ForYouDishCard } from './ForYouDishCard';
import { ForYouPrompt } from './ForYouPrompt';

const LIMIT = 12;

const useStyles = makeStyles(theme => ({
  dishGrid: {
    display: 'grid',
    // Matches the place feed's spacing, so cards keep their size when switching tabs
    gap: theme.spacing(6, 4),
    gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: '1fr',
    },
  },
  loaderGrid: {
    // The grid's row gap, so the skeleton sits a row below the last dishes
    marginTop: theme.spacing(6),
  },
  muted: {
    color: theme.palette.text.secondary,
  },
  skeleton: {
    borderRadius: theme.spacing(1),
    height: 0,
    paddingTop: '75%',
  },
}));

const DishGridSkeleton = ({ isLoader = false }: { isLoader?: boolean }) => {
  const classes = useStyles();

  return (
    <Box aria-busy="true" className={isLoader ? `${classes.dishGrid} ${classes.loaderGrid}` : classes.dishGrid}>
      {Array.from({ length: 4 }, (_, index) => <Skeleton key={index} className={classes.skeleton} variant="rect" />)}
    </Box>
  );
};

// The place's dishes ranked for the signed-in diner, once they've played a round of Refine your palate
const RankedDishes = ({ placeSlug }: { placeSlug: string }) => {
  const classes = useStyles();
  const refinePath = useRefinePath();
  const loadMoreOnce = useLoadMoreOnce();
  const { data, error, fetchMore } = useRootPlacePersonalizedMenuItemsQuery({
    fetchPolicy: 'network-only',
    ssr: false,
    variables: { pagination: { limit: LIMIT, offset: 0 }, slug: placeSlug },
  });

  if (!data && error) {
    return (
      <Typography className={classes.muted}>
        <FormattedMessage defaultMessage="We couldn't load your dishes. Please try again." />
      </Typography>
    );
  }
  if (!data) return <DishGridSkeleton />;

  if (!data.userTasteProfile.hasCompletedRefineRound) {
    return (
      <ForYouPrompt
        body={(
          <FormattedMessage
            defaultMessage="Pick between two dishes 15 times and we'll rank the dishes here for your taste."
          />
        )}
        buttonText={<FormattedMessage defaultMessage="Play a round" />}
        title={<FormattedMessage defaultMessage="Play a round of Refine your palate first" />}
        to={refinePath ?? '/refine'}
      />
    );
  }

  const personalizedMenuItems = data.rootPlace?.personalizedMenuItems;
  if (!personalizedMenuItems?.records.length) {
    return (
      <Typography className={classes.muted}>
        <FormattedMessage defaultMessage="No dishes here match your taste preferences yet." />
      </Typography>
    );
  }

  const nextOffset = personalizedMenuItems.offset + LIMIT;

  return (
    <InfiniteScroll
      hasMore={personalizedMenuItems.hasMore}
      loadMore={() => {
        loadMoreOnce(`${placeSlug}:${nextOffset}`, () => fetchMore({
          updateQuery: (previousResult, { fetchMoreResult }) => {
            if (!previousResult.rootPlace || !fetchMoreResult.rootPlace) return fetchMoreResult;

            const finalResult = fetchMoreResult;
            finalResult.rootPlace.personalizedMenuItems.records = [
              ...previousResult.rootPlace.personalizedMenuItems.records,
              ...fetchMoreResult.rootPlace.personalizedMenuItems.records,
            ];
            return finalResult;
          },
          variables: { pagination: { limit: LIMIT, offset: nextOffset }, slug: placeSlug },
        }));
      }}
      loader={<DishGridSkeleton key="loader" isLoader />}
      threshold={LOAD_MORE_THRESHOLD}
      useWindow
    >
      <Box className={classes.dishGrid}>
        {personalizedMenuItems.records.map(userTasteMatch => (
          <ForYouDishCard
            key={userTasteMatch.menuItem.id}
            dishPathPrefix={`/places/${placeSlug}/for-you`}
            userTasteMatch={userTasteMatch}
          />
        ))}
      </Box>
    </InfiniteScroll>
  );
};

export const ForYouPage = () => {
  const placeSlug = useRouteParams().placeSlug ?? '';
  const signInPath = useSignInPath();
  const { loading, user } = useEatSession();

  return (
    <React.Fragment>
      <Helmet>
        <meta name="robots" content="noindex" />
      </Helmet>
      <DishFeedTitle isForYou isHomePage={false} placeSlug={placeSlug} />
      <DishFeedTabs
        feedPath={`/places/${placeSlug}`}
        forYouPath={`/places/${placeSlug}/for-you`}
        selected="forYou"
      />
      {!loading && !user && (
        <ForYouPrompt
          body={(
            <FormattedMessage
              defaultMessage="Sign in and play a quick round of picks, and we'll rank the dishes here for your taste."
            />
          )}
          buttonText={<FormattedMessage defaultMessage="Sign In" />}
          title={<FormattedMessage defaultMessage="See the dishes you'd like most" />}
          to={signInPath}
        />
      )}
      {user && <RankedDishes placeSlug={placeSlug} />}
    </React.Fragment>
  );
};
